import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { CalendarClock, Loader2, Check, X, Send, Phone, MapPin, AlertCircle, RefreshCw, Lock, Sparkles } from 'lucide-react';

/**
 * ByAppointmentRequestsTab — list + manage on-request appointment asks for
 * hospitals where the doctor visits only when a patient specifically asks.
 *
 * Workflow: pending → (doctor offers 30-min window) → offered →
 *   (patient confirms with no-show ack) → confirmed
 * Or: pending → declined / cancelled.
 *
 * Polls every 15s; plays a chime + flashes the page title when a new
 * pending row arrives so the doctor notices even on a backgrounded tab.
 */
export default function ByAppointmentRequestsTab() {
  const { user } = useAuth();
  const isTurbo = (user?.tier || 'free') === 'turbo';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // pending | offered | confirmed | all
  const [offerFor, setOfferFor] = useState(null);

  // Chime + tab-title flash on new pending arrivals.
  const prevPendingIds = useRef(new Set());
  const originalTitle = useRef(typeof document !== 'undefined' ? document.title : 'DocSite');
  const titleFlashTimer = useRef(null);

  // Two-note rising chime synthesised via Web Audio — no external asset.
  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const note = (freq, start, durMs) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.value = 0;
        gain.gain.linearRampToValueAtTime(0.22, now + start + 0.01);
        gain.gain.linearRampToValueAtTime(0, now + start + (durMs / 1000));
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + (durMs / 1000) + 0.05);
      };
      note(880, 0.0, 200);
      note(1318.5, 0.2, 260);
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 900);
    } catch { /* autoplay blocked — silent */ }
  };

  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!isTurbo) { setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/dashboard/appointment-requests');
      const entries = data.entries || [];
      // Detect new pending IDs since last poll → play chime.
      const currentPendingIds = new Set(entries.filter(e => e.status === 'pending').map(e => e.id));
      const isFirstLoad = prevPendingIds.current.size === 0 && rows.length === 0;
      if (!isFirstLoad) {
        const newOnes = [...currentPendingIds].filter(id => !prevPendingIds.current.has(id));
        if (newOnes.length > 0) {
          playChime();
          if (typeof document !== 'undefined') {
            document.title = `🔔 ${newOnes.length} new appointment request${newOnes.length > 1 ? 's' : ''}`;
            clearTimeout(titleFlashTimer.current);
            titleFlashTimer.current = setTimeout(() => { document.title = originalTitle.current; }, 8000);
          }
        }
      }
      prevPendingIds.current = currentPendingIds;
      setRows(entries);
    } catch (_) { /* keep last rows */ }
    finally { setLoading(false); }
  }, [rows.length]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    const t = setInterval(() => fetchAll({ silent: true }), 15000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const filtered = rows.filter(r => tab === 'all' ? true : r.status === tab);
  const countOf = (s) => rows.filter(r => r.status === s).length;

  const handleDecline = async (r) => {
    const reason = window.prompt(`Decline request from ${r.patient_name}?\n\nOptional reason shown to patient:`, '');
    if (reason === null) return;
    try {
      await api.post(`/dashboard/appointment-requests/${r.id}/decline`, { reason });
      fetchAll();
    } catch (e) { console.warn('decline failed', e); }
  };

  return (
    <div className="space-y-4" data-testid="by-appointment-requests-tab">
      {!isTurbo && (
        <div className="rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-amber-50 p-6 text-center max-w-2xl mx-auto" data-testid="by-appt-upgrade-card">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-violet-600 text-white mb-3">
            <Sparkles size={11} /> Turbo plan feature
          </div>
          <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            By Appointment — On Request workflow
          </h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-md mx-auto">
            Designed for honorary consultants & visiting specialists. Patients ping you for an exclusive visit; you offer a 30-min window in one tap. No-show penalty + branded QR pass included.
          </p>
          <ul className="text-xs text-slate-700 text-left max-w-sm mx-auto mt-4 space-y-1.5">
            <li className="flex items-start gap-2"><Check size={13} className="text-violet-600 mt-0.5" /> Live patient-ping with audible chime</li>
            <li className="flex items-start gap-2"><Check size={13} className="text-violet-600 mt-0.5" /> OTP-verified patient pass with QR for reception</li>
            <li className="flex items-start gap-2"><Check size={13} className="text-violet-600 mt-0.5" /> ₹500 no-show fee enforced on next visit</li>
            <li className="flex items-start gap-2"><Check size={13} className="text-violet-600 mt-0.5" /> Per-location On-request mode toggle</li>
          </ul>
          <button onClick={() => { window.location.href = '/dashboard?tab=wallet'; }}
            className="mt-5 inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700"
            data-testid="by-appt-upgrade-cta">
            <Lock size={13} /> Upgrade to Turbo
          </button>
          <p className="text-[10px] text-slate-400 mt-3">Already on Turbo? Log out + back in to refresh your plan.</p>
        </div>
      )}
      {isTurbo && (
        <>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <CalendarClock size={18} className="text-amber-600" /> By Appointment Requests
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Patients ping you for an exclusive visit at on-request hospitals — offer a 30-min window or decline.
          </p>
        </div>
        <button onClick={() => fetchAll()} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50" data-testid="by-appt-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'pending',   label: 'Pending',   color: 'amber' },
          { key: 'offered',   label: 'Offered',   color: 'sky'   },
          { key: 'confirmed', label: 'Confirmed', color: 'emerald' },
          { key: 'all',       label: 'All',       color: 'slate' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-colors ${tab === t.key ? `bg-${t.color}-600 text-white border-${t.color}-600` : `bg-white text-slate-600 border-slate-200 hover:border-slate-400`}`}
            data-testid={`by-appt-tab-${t.key}`}>
            {t.label}{t.key !== 'all' && countOf(t.key) > 0 ? ` · ${countOf(t.key)}` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500" data-testid="by-appt-empty">
          <CalendarClock size={26} className="mx-auto text-slate-300 mb-2" />
          <p className="font-medium">No {tab === 'all' ? '' : tab} requests right now.</p>
          <p className="text-xs text-slate-400 mt-1">Patients will reach you here when they ping an on-request hospital location.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="by-appt-grid">
          {filtered.map(r => (
            <RequestCard key={r.id} r={r}
              onOffer={() => setOfferFor(r)}
              onDecline={() => handleDecline(r)}
              onRefresh={() => fetchAll()} />
          ))}
        </div>
      )}

      {offerFor && (
        <OfferSlotModal r={offerFor} onClose={() => setOfferFor(null)} onSaved={() => { setOfferFor(null); fetchAll(); }} />
      )}
        </>
      )}
    </div>
  );
}


function StatusChip({ status }) {
  const map = {
    pending:   { bg: 'bg-amber-50',   color: 'text-amber-700',   border: 'border-amber-200',   label: 'Pending' },
    offered:   { bg: 'bg-sky-50',     color: 'text-sky-700',     border: 'border-sky-200',     label: 'Slot offered' },
    confirmed: { bg: 'bg-emerald-50', color: 'text-emerald-700', border: 'border-emerald-200', label: 'Confirmed' },
    declined:  { bg: 'bg-rose-50',    color: 'text-rose-700',    border: 'border-rose-200',    label: 'Declined' },
    cancelled: { bg: 'bg-slate-100',  color: 'text-slate-500',   border: 'border-slate-200',   label: 'Cancelled' },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${s.bg} ${s.color} ${s.border}`}>
      {s.label}
    </span>
  );
}


function RequestCard({ r, onOffer, onDecline, onRefresh }) {
  const dateLabel = (r.created_at || '').slice(0, 10);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`by-appt-row-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-slate-900 text-sm truncate">{r.patient_name}</h4>
            <StatusChip status={r.status} />
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-1">
            <Phone size={11} /> {r.patient_phone || (r.patient_phone_last4 ? `●●●●●● ${r.patient_phone_last4}` : '—')}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
            <MapPin size={11} /> {r.location_name}
          </div>
        </div>
        <div className="text-[10px] text-slate-400 whitespace-nowrap">{dateLabel}</div>
      </div>

      {r.reason && (
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5 text-[11px] text-slate-700">
          <span className="font-semibold text-slate-500 mr-1">Reason:</span>{r.reason}
        </div>
      )}

      {(r.preferred_days || []).length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">Patient prefers</span>
          {r.preferred_days.map(d => (
            <span key={d} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100">{d}</span>
          ))}
        </div>
      )}

      {r.offered_slot && (
        <div className="mt-2 rounded-lg bg-sky-50 border border-sky-200 px-2.5 py-1.5 text-[11px] text-sky-800">
          <span className="font-semibold mr-1">Offered:</span>
          {r.offered_slot.date} · {r.offered_slot.start_time}–{r.offered_slot.end_time} ({r.offered_slot.duration_minutes} min)
        </div>
      )}

      {r.decline_reason && (
        <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-2.5 py-1.5 text-[11px] text-rose-800">
          <span className="font-semibold mr-1">Decline note:</span>{r.decline_reason}
        </div>
      )}

      {r.status === 'pending' && (
        <div className="mt-3 flex gap-2">
          <button onClick={onOffer} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700" data-testid={`by-appt-offer-${r.id}`}>
            <Send size={12} /> Offer 30-min slot
          </button>
          <button onClick={onDecline} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50" data-testid={`by-appt-decline-${r.id}`}>
            <X size={12} /> Not available
          </button>
        </div>
      )}

      {r.status === 'offered' && (
        <div className="mt-3 flex gap-2">
          <button onClick={onOffer} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-sky-200 text-sky-700 text-xs font-semibold hover:bg-sky-50" data-testid={`by-appt-reoffer-${r.id}`}>
            Re-offer
          </button>
          <span className="inline-flex items-center text-[11px] text-slate-500 ml-auto">
            <AlertCircle size={11} className="text-amber-500 mr-1" /> Waiting for patient confirmation
          </span>
        </div>
      )}

      {r.status === 'confirmed' && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold">
          <Check size={12} /> Frozen on your calendar
        </div>
      )}
    </div>
  );
}


function OfferSlotModal({ r, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState((r.preferred_days || [])[0] || today);
  const [startTime, setStartTime] = useState('11:00');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const computedEnd = (() => {
    const [hh, mm] = startTime.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return '';
    const total = hh * 60 + mm + 30;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  })();

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await api.post(`/dashboard/appointment-requests/${r.id}/offer-slot`, { date, start_time: startTime });
      onSaved && onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to offer slot');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="offer-slot-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <h4 className="font-semibold text-slate-900 text-base">Offer a 30-min window</h4>
          <p className="text-xs text-slate-500 mt-1">for <b className="text-slate-700">{r.patient_name}</b> at <b className="text-slate-700">{r.location_name}</b></p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-700 mb-1 block">Day</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
              data-testid="offer-date" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-700 mb-1 block">Start time</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
              data-testid="offer-start-time" />
          </div>
        </div>
        <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-[12px] text-sky-800">
          Patient sees:&nbsp;<b className="font-mono">{date} · {startTime}–{computedEnd}</b> &nbsp;(2 back-to-back 15-min slots).
        </div>
        {err && <p className="text-xs text-rose-600">{err}</p>}
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving} className="flex-1 rounded-full px-4 py-2 bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50" data-testid="offer-save">
            {saving ? 'Sending…' : 'Send offer to patient'}
          </button>
          <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-800 px-3 py-2">Cancel</button>
        </div>
      </div>
    </div>
  );
}
